/* eslint-disable no-case-declarations */
import head from 'lodash/head';
import { initialStateBase, reducer } from './newCollection';
import { paramsToQueryString, getUrlParams, queryStringToParams } from '../../helpers/url';
import { ITEMS_SEARCH_SOURCE, ITEMS_FEED_SOURCE, APIVERSION } from 'Constants/items';
import { remove as removeRecentSearches, add as addRecentSearch } from 'Helpers/recentSearches';
import { TYPE as COVER_TYPE } from './cover';
import { getType } from './helpers/typeModifier';
import actionConstants from 'Actions/constants';

export const urls = {
    getItems: (itemsSource, apiVersion = APIVERSION.V1) => {
        if (itemsSource === ITEMS_SEARCH_SOURCE) {
            if (apiVersion === APIVERSION.V4) {
                return '/api/relevance/v4/search';
            }
            return '/api/relevance/search';
        }
        if (itemsSource === ITEMS_FEED_SOURCE) {
            return '/api/relevance/v4/feed';
        }
        return '/api/items';
    },
    getItem: id => `/api/v3/items/${id}?flagEntities=monetization`,
    getBulkItems: '/api/v3/items/bulk',
    getItemPhone: id => `/api/v3/items/${id}/params?filter=ad_phone`,
    getItemDetail: (id, version = APIVERSION.V2) => `/api/bxp/${version}/items/${id}`,
    getUserFavAds: userId => `/api/cupid/users/${userId}/favourites`,
    getUserAds: id => `/api/v3/users/${id}/items`,
    getUserBoughtAds: id => `/api/users/${id}/bought`,
    getUserAdsToRelocate: id => `/api/users/${id}/relocate`,
    favoriteIt: userId => `/api/v2/cupid/users/${userId}/favourites`,
    removeFavorite: (userId, itemId, categoryId, dealerType) => `/api/v2/cupid/users/${userId}/favourites/${itemId}?categoryId=${categoryId}&dealerType=${dealerType}`,
    isFavorited: (userId, itemId) => `/api/cupid/users/${userId}/favourites/${itemId}`,
    editItem: itemId => `/api/v5/items/${itemId}`,
    postItem: '/api/v5/items',
    postItemWithSL: sl => `/api/v5/items?sessionLong=${sl}`,
    postImage: '/api/v2/images',
    postSIImage: '/api/inspection/image',
    deleteItem: itemId => `/api/items/${itemId}`,
    relocateItems: '/api/items/migratelocation',
    recommendations: (userId, adId, categoryId) => `/api/relevance/recommendations?user=${userId}&ad_id=${adId}&category_id=${categoryId}`,
    preferredAdsFromPremiumUsers: (userId, adId, categoryId) => `/api/relevance/preferredAds?user=${userId}&ad_id=${adId}&category_id=${categoryId}`,
    similarAdsFromPremiumUsers: (userId, adId, categoryId) => `/api/relevance/similarAds?user=${userId}&ad_id=${adId}&category_id=${categoryId}`,
    trackSearch: '/api/bxp/searches',
    bundles: '/api/bxp/bundles',
    bundlesV2: '/api/bxp/v2/feed/bundles',
    getBundleItems: (dataUrl, locationId, locationLatitude, locationLongitude, platform, userId, sessionId) => `${dataUrl}&locationId=${locationId}&locationLatitude=${locationLatitude}&locationLongitude=${locationLongitude}&platform=${platform}&userId=${userId}&sessionId=${sessionId}`,
    getRelationshipValidation: (adId, buyerId) => `/api/v3/relationships/validate?adId=${adId}&buyerId=${buyerId}`,
    createRelationship: '/api/v3/relationships',
    getBannerSrp: '/api/bxp/banner-configurations/search'
};

export const cleanImages = images =>
    images.map(({ full, big, medium, small, ...rest }) => rest); // eslint-disable-line no-unused-vars

/**
 *
 * @param {*} data
 */
const sanitizeImages = data => data.map(item => {
    if (item && item.images && item.images.length) {
        item.images = cleanImages(item.images);
    }
    return item;
});

export const filterParameters = ({ page, cursor, ...params } = {}) => params; // eslint-disable-line no-unused-vars
export const getCollectionKey = (params, relevanceSearch = false) => `${urls.getItems(relevanceSearch)}?${paramsToQueryString(filterParameters(params))}`;

export const initialState = {
    ...initialStateBase,
    metadataKey: 'ads',
    lastCollectionUrl: '',
    collectionList: [],
    escrow_details_seller: {},
    aditionalMetadataKeys: [
        'filters',
        'links',
        'appliedFilters',
        'applied_sorting',
        'sortingOptions',
        'feed_version',
        'removed_term',
        'total_similar_ads',
        'applied_filters',
        'modified_filters',
        'modified_term',
        'sections',
        'suggested_sections',
        'suggestions_offset',
        'original_term',
        'suggested_term',
        'original_label',
        'suggested_label',
        'show_suggested_items',
        'show_original_items',
        'total_pages',
        'show_hint',
        'hint',
        'total_suggested_ads',
        'total_suggested_pages',
        'suggested_ads_on_page',
        'resultset_id',
        'unfiltered_total',
        'showDownloadLeads',
        'applicable_suggestions',
        'unavailable_boosted_year',
        'users'
    ],
    dataSrpListItemBanner: {}
};

const isFavoritedUrl = url => {
    const urlWithoutParams = url.split('?')[0];
    const urlParams = getUrlParams(urlWithoutParams);

    return urlWithoutParams === urls.isFavorited(urlParams[0], urlParams[1]);
};

const suggestedDataExists = resp =>
    !!(resp && resp.metadata && resp.metadata.show_suggested_items && resp.suggested_data && resp.suggested_data.length);

/**
 *  Tranforms the api response into a structure suitable for collection reducer
 * @param {*} state - The Redux Store for items
 * @param {*} url - the api from which the items were fetched
 * @param {*} response - api response
 */
const transformGetData = (_, url, response) => {
    // the favorited url i guess give a different response
    if (isFavoritedUrl(url)) {
        response = {
            data: response
        };
    }

    if (Array.isArray(response?.data)) {
        if (suggestedDataExists(response)) {
            const suggestionOffset = response.data.length;

            response.data = [...response.data, ... response.suggested_data];
            response.metadata.suggestions_offset = suggestionOffset;
            delete response.suggested_data;
        }
        response.data = sanitizeImages(response.data);
    }

    return response;
};

const transformElementData = (state, { data, metadata }) => {
    if (state.elements[data.id]) {
        const elementMonetizationInfo = state.elements[data.id].monetizationInfo;

        data = { ...state.elements[data.id], ...data };

        if (elementMonetizationInfo && !data.monetizationInfo) {
            data.monetizationInfo = elementMonetizationInfo;
        }
    }

    const response = {
        data: {
            ...data,
            is_full: true,
            metadata: {
                category: (metadata && metadata.category) ? metadata.category : (data.metadata && data.metadata.category) || {},
                locations: (metadata && metadata.locations) ? metadata.locations : (data.metadata && data.metadata.locations) || {}
            }
        },
        metadata
    };

    if (response.data && Array.isArray(response.data.images) && response.data.images.length) {
        response.data.images = cleanImages(response.data.images);
    }

    return response;
};

const transformPostData = (state, url, response) => {
    const urlWithoutParams = url.split('?')[0];
    const urlParams = getUrlParams(url);

    switch (urlWithoutParams) {
        case urls.favoriteIt(urlParams[0]): {
            const userId = urlParams[0];
            const itemId = head(response?.data)?.adId;
            const type = getType('ITEMS', urls.isFavorited(userId, itemId));

            state.collections[type] = true;

            break;
        }
    }

    return response;
};

/**
 * This function takes the spellcheck metadata flag from the previous url response and
 * and adds them to the current url reponse's metadata
 * because in the subsequent url calls for a listing/category page, service may not return spellcheck flags
 * this function works only if the collectonList has more than 1 url in the array
 * reason explain inside the function
 * @param {*} collectionMetadata - the metadata of the state
 * @param {*} collectionList - the list of collections for which data has been fetched
 */
const getUpdatedSpellcheckFlags = ({ collectionMetadata, collectionList }) => {
    const collectionListLength = collectionList.length;

    // if collectionList has 1 url
    // then return the metadata as it is.
    // because the spellcheck flags obtained will followed
    if (collectionListLength < 2) {
        return collectionMetadata;
    }

    // if there are atleast 2 urls in the collectionList
    // that means the current url fetch was for subsequent/'load more' call for a listing/category page
    // update the current url response metadata flag with the last/previous url call
    // as flags in current url response might be inconsistent
    const previousUrl = collectionList[collectionListLength - 2];
    const currentUrl = collectionList[collectionListLength - 1];

    // following are the spellcheck flags
    const prevType = getType('ITEMS', previousUrl);
    const currType = getType('ITEMS', currentUrl);

    const {
        show_hint,
        show_suggested_items,
        show_original_items,
        original_label,
        suggested_label
    } = collectionMetadata[prevType];

    return {
        ...collectionMetadata,
        [currType]: {
            ...collectionMetadata[currType],
            ...{ show_hint, show_suggested_items, show_original_items, original_label, suggested_label }
        }
    };
};

const transformDeleteData = (state, url, response, params) => {
    const urlStr = url.split('?')[0];
    const urlParams = getUrlParams(urlStr);
    const urlQueryParams = queryStringToParams(params);

    switch (url) {
        case `${urls.removeFavorite(urlParams[0], urlParams[1], urlQueryParams.categoryId, urlQueryParams.dealerType) }?${params}`:
        case `${urls.removeFavorite(urlParams[0], urlParams[1], urlQueryParams.categoryId, urlQueryParams.dealerType) }&lang=${urlQueryParams.lang}`:
        case urls.removeFavorite(urlParams[0], urlParams[1], urlQueryParams.categoryId, urlQueryParams.dealerType): {
            const userId = urlParams[0];
            const itemId = urlParams[1];
            const type = getType('ITEMS', urls.isFavorited(userId, itemId));

            state.collections[type] = false;

            break;
        }
        case `${urls.deleteItem(urlParams[0])}?${params}`: {
            const itemId = urlParams[0];
            const userId = state.elements[itemId].user_id;
            const userAdsUrl = urls.getUserAds(userId);
            const type = getType('ITEMS', userAdsUrl);
            const reason = queryStringToParams(params).reason;

            if (state.collections[type] && reason !== 'sold') {
                state.collections[type] = state.collections[type].filter(id => id !== itemId);
            }

            if (reason !== 'sold') {
                delete state.elements[itemId];
            }
        }
    }

    return response;
};

const items = (state = initialState, action) => {
    switch (action.type) {
        case 'TEST_ACTION':
            return {
                ...state,
                calledFromLib: true
            };
        case 'RESET_ITEMS_LIST':
            return {
                ...state,
                collectionList: []
            };
        case 'SET_ITEMS_LIST':
            return {
                ...state,
                collectionList: [action.url]
            };
        case 'MERGE_ITEMS_LIST':
            return {
                ...state,
                collectionList: [...state.collectionList, action.url]
            };
        case 'REQUEST_ITEMS':
            return {
                ...reducer(state, action, 'ITEMS')
            };
        case 'UPDATE_COLLECTION_URL':
            return {
                ...state,
                lastCollectionUrl: action.url
            };
        case 'RECEIVE_ITEMS':
            action.data = transformGetData(state, action.url, action.data);
            const searchTitle = action.data?.metadata?.suggested_term || action.data?.metadata?.original_term;
            const itemCount = action.data?.metadata?.total_suggested_ads || action.data?.metadata?.total_ads;

            if (searchTitle && itemCount) {
                addRecentSearch({ title: searchTitle });
            }
            else if (searchTitle && !itemCount) {
                removeRecentSearches(searchTitle);
            }
            if (action.url.includes('/relevance/') && !action.url.includes('/recommendation')) {
                const reducedState = {
                    ...reducer(state, action, 'ITEMS'),
                    collectionList: [
                        ...state.collectionList,
                        action.url
                    ]
                };

                return {
                    ...reducedState,
                    collectionMetadata: getUpdatedSpellcheckFlags(reducedState)
                };
            }
            return reducer(state, action, 'ITEMS');
        case 'RESPONSE_POST_ITEMS':
        case 'RESPONSE_PUT_ITEMS':
            action.data = transformPostData(state, action.url, action.data, action.params);
            return reducer(state, action, 'ITEMS');
        case 'RESPONSE_DELETE_ITEMS':
            action.data = transformDeleteData(state, action.url, action.data, action.params);
            return reducer(state, action, 'ITEMS');
        case 'RECEIVE_ELEMENT_ITEMS':
            action.data = transformElementData(state, action.data);
            return reducer(state, action, 'ITEMS');
        case `RECEIVE_${COVER_TYPE}`:
            return action.data.data.reduce((state, section) => {
                if (section.type !== 'item-carousel') {
                    return state;
                }

                return {
                    ...items(state, {
                        type: 'RECEIVE_ITEMS',
                        url: section.url,
                        data: section.items
                    }),
                    lastCollectionUrl: initialState.lastCollectionUrl,
                    collectionList: initialState.collectionList
                };
            }, state);
        case 'RECEIVE_COLLECTION_ITEMS':
            action.data = transformGetData(state, action.url, action.data);
            return reducer(state, action, 'COLLECTION_ITEMS');
        case `CUSTOM_RECEIVE_${actionConstants.CAR_RESERVE_STATUS}`:
            return {
                ...state,
                elements: {
                    ...state.elements,
                    ...(state.elements[action.adId] ? { [action.adId]: {
                        ...state.elements[action.adId],
                        reserve: {
                            status: action?.data?.car?.status,
                            expiryDate: action?.data?.car?.reservationExpiry,
                            isReservedByCurrentUser: action?.data?.isReservedByCurrentUser,
                            hasReservedAnyCar: action?.data?.user?.hasReservedAnyCar
                        }
                    }
                    } : {})
                }
            };
        case 'RECEIVE_GET_DETAIL_ESCROW':
            return {
                ...state,
                escrow_details_seller: action.data.data.escrow_details
            };
        case 'REQUEST_GET_BANNER_SRP': {
            const newState = reducer(state, action, 'GET_BANNER_SRP');

            return {
                ...newState

            }; }
        case 'RECEIVE_GET_BANNER_SRP': {
            const newState = reducer(state, action, 'GET_BANNER_SRP');
            const urlQueryParams = queryStringToParams(action.params);
            const { categoryId, platform } = urlQueryParams;

            return {
                ...newState,
                dataSrpListItemBanner: {
                    ...state.dataSrpListItemBanner,

                    [platform]: {
                        ...(state.dataSrpListItemBanner[platform] || {}),
                        // Salin objek untuk 'categoryId' yang spesifik, lalu tambahkan/timpa 'data' dan 'lastFetched'
                        [categoryId]: {
                            ...(state.dataSrpListItemBanner[platform]?.[categoryId] || {}), // Ini penting: salin dulu data categoryId yang mungkin sudah ada (meskipun belum ada di skema kita, ini praktik yang bagus)
                            data: action?.data?.data ?? [] // Tambahkan/timpa properti data
                        }
                    }}};
        } default:
            return reducer(state, action, 'ITEMS');
    }
};

export default items;
